import { useState, useEffect } from 'react';

/**
 * Hook to progressively load images
 * Loads a low-quality placeholder first, then swaps to high-quality when loaded
 */
export function useProgressiveImage(lowQualitySrc: string, highQualitySrc: string) {
    const [src, setSrc] = useState(lowQualitySrc);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Always start with low quality immediately
        setSrc(lowQualitySrc);
        setIsLoading(true);

        // If low and high quality are the same, no need to load twice
        if (lowQualitySrc === highQualitySrc) {
            setIsLoading(false);
            return;
        }

        // Preload high quality image in the background
        const img = new Image();
        img.src = highQualitySrc;

        img.onload = () => {
            // Only swap to high quality after it's fully loaded
            setSrc(highQualitySrc);
            setIsLoading(false);
        };

        img.onerror = () => {
            // If high quality fails to load, keep the low quality version
            setIsLoading(false);
        };

        return () => {
            // Cleanup
            img.onload = null;
            img.onerror = null;
        };
    }, [lowQualitySrc, highQualitySrc]);

    return { src, isLoading };
}
